// packages/web-common/src/plugins/PluginRoutes.tsx
import type { ReactElement } from 'react';
import { Route } from 'react-router-dom';
import { usePluginPages } from './usePluginPages';
import { PluginPageHost } from './PluginPageHost';

interface PluginRoutesOptions {
  /**
   * What to render for an unmatched path once plugin pages are known.
   * Typically `<Navigate to="/dashboard" replace />`.
   *
   * The host app MUST pass its catch-all here instead of declaring its own
   * `<Route path="*">`, because the catch-all has to lose to a plugin route
   * that has not loaded yet. See below.
   */
  fallback: ReactElement;
}

/**
 * Returns a fragment of <Route> elements — ONE per registered plugin page,
 * plus the app's catch-all — to be placed INSIDE the host SPA's <Routes>.
 *
 * Usage in App.tsx:
 *   <Routes>
 *     <Route path="/dashboard" element={<Dashboard />} />
 *     ...host routes...
 *     {PluginRoutes({ fallback: <Navigate to="/dashboard" replace /> })}
 *   </Routes>
 *
 * react-router-dom v6 requires <Route> elements to be direct children of
 * <Routes>; rendering them through a function-call helper is the cleanest way
 * to keep the wiring boilerplate inside the host SPA tiny.
 *
 * ## Why the catch-all lives here
 *
 * Plugin pages are discovered ASYNCHRONOUSLY from `/api/plugins/pages`. On the
 * first render the list is empty, so a deep link to `/admin/plugins/foo/bar` —
 * a bookmark, a refresh, or an opened-in-new-tab link — matched nothing, hit
 * the app's `<Route path="*">`, and was redirected to the dashboard before the
 * plugin route ever existed. The user only got to a plugin page by clicking
 * through from an already-warm SPA, and reloading kicked them out again.
 *
 * `usePluginPages`'s docstring records the same symptom from 2026-05-26; the
 * fix applied then changed WHEN the fetch happens (post-authentication) and
 * left the race with the catch-all in place.
 *
 * Owning the catch-all lets it be suppressed while discovery is in flight:
 * during `loading` an unmatched path renders a neutral placeholder and the URL
 * is left ALONE, so when the routes arrive the router re-matches and the
 * plugin page mounts at the address the user actually asked for.
 */
export function PluginRoutes({ fallback }: PluginRoutesOptions) {
  const { pages, loading, error } = usePluginPages();
  return (
    <>
      {pages.map((page) => (
        <Route
          key={`${page.plugin_id}:${page.path}`}
          path={page.path}
          element={<PluginPageHost page={page} />}
        />
      ))}
      <Route
        path="*"
        element={
          loading ? (
            <div
              data-testid="plugin-routes-loading"
              className="flex items-center justify-center py-16"
            >
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          ) : error ? (
            <div role="alert" className="p-6 space-y-3">
              <p>Unable to load plugin pages. Please try again.</p>
              <button type="button" className="btn-primary" onClick={() => window.location.reload()}>Retry</button>
            </div>
          ) : (
            fallback
          )
        }
      />
    </>
  );
}
